import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Landmark, Globe, BarChart3, Shield, MessageSquare, FileText, Leaf, CheckCircle, Users, Lock, Settings, ClipboardList, Eye, Zap, LayoutDashboard, Building2, TrendingUp, Bell } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageCSSEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - CSS (HQ) | Governance & Policy"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="css" lang="en" />

      {/* Hero CSS */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Landmark size={16} />
            <span>CSS Portal (School Service Center)</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-css)' }}>CSS (HQ)</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            <strong style={{ color: 'var(--actor-css)' }}>Global food governance</strong><br />
            Strategic policy and centralized management
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">360°</div>
              <div className="stat-label">Global view</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Traceability</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">BI</div>
              <div className="stat-label">Built-in analytics</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="section" data-testid="manifeste-css">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoLunch for School Service Centers?</h2>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-css-centralized">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#E74C3C'}}><Globe size={30} /></div>
              <h3>Strategic centralization of food governance</h3>
            </div>
            <p>School Service Centers oversee dozens of schools, administrations, and caterers with often fragmented tools. EcoLunch provides a <strong>unified governance portal</strong> that consolidates all performance, compliance, and food sustainability data in real time.</p>
            <p>A single executive dashboard to manage your entire network, define food policies, and <strong>make informed strategic decisions</strong> with reliable and consolidated data.</p>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-css-sovereignty">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}><Lock size={30} /></div>
              <h3>Data sovereignty and guaranteed compliance</h3>
            </div>
            <p>All your school network data is hosted in Quebec and Canada by <strong>OVH Cloud</strong> (Montreal & Toronto). No data is transferred abroad.</p>
            <p>Nutritional standards and policies are defined at the CSS level and automatically verified for each institution, ensuring <strong>complete and traceable compliance</strong> during audits.</p>
          </div>
        </div>
      </section>

      {/* Governance Center */}
      <section id="gouvernance" className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Governance Center</h2>
            <p className="section-subtitle">
              Complete centralization of school food governance
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Globe size={32} /></div>
              <h3>Global View</h3>
              <p>All administrations, all schools, all caterers. 360° vision of your complete food network.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Comparisons</h3>
              <p>Performance, quality, compliance. Analyze and compare each institution in your network.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Governance</h3>
              <p>Strategic indicators, critical alerts, dashboards. Manage with precision.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Communication</h3>
              <p>Policies, directives, standards. Broadcast your guidelines across the entire network.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Reports</h3>
              <p>Monthly, quarterly, annual. Customizable and automatable for your committees.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Leaf size={32} /></div>
              <h3>Sustainability</h3>
              <p>Healthy menus, waste reduction, local products. Track your environmental commitments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Flexible Governance */}
      <section className="section roi-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Flexible Governance</h2>
            <p className="section-subtitle">
              Control adapted to your organization's needs
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Total Control</h3>
              <p>All permissions and access under your control. Define who sees what and who does what.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Direct Communication</h3>
              <p>With caterers directly or through school administrations. Choose the appropriate channel.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Flexible Delegation</h3>
              <p>Delegate to school administrations as needed. Keep control while empowering.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Settings size={32} /></div>
              <h3>Configurable Rules</h3>
              <p>Escalation, alerts, and reports are configurable. Adapt the system to your governance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Journaling */}
      <section className="section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Journaling</h2>
            <p className="section-subtitle">
              Complete traceability and guaranteed transparency
            </p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric">
                <ClipboardList size={40} />
                <h3>Traceable Actions</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                All actions performed in the system are recorded and traceable. Who did what, when, and why.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <FileText size={40} />
                <h3>Complete Audit</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Reading, modification, communication: every interaction is logged. Ready for any internal or external audit.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <CheckCircle size={40} />
                <h3>Guaranteed Compliance</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Compliance and transparency guaranteed at every level of your organization. Automatic documentation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Frontend Web Interface */}
      <section className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Frontend Web Interface</h2>
            <p className="section-subtitle">
              A dedicated web portal for CSS administrators
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><LayoutDashboard size={32} /></div>
              <h3>Real-Time Executive Dashboard</h3>
              <p>Instant overview of your network's performance. KPIs, trends, and alerts at a glance.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Building2 size={32} /></div>
              <h3>Multi-School & Admin Management</h3>
              <p>Navigate easily between your institutions. Compare, analyze, and make informed decisions.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Consolidated Reports & KPIs</h3>
              <p>Customizable automatic reports. Export in one click for your committees and boards.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Settings size={32} /></div>
              <h3>Policy Configuration</h3>
              <p>Define your nutritional, budgetary, and operational rules from a centralized interface.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Analytics & Business Intelligence</h3>
              <p>Advanced analytics, forecasts, and modeling. Transform your data into decision intelligence.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Alerts & Notifications</h3>
              <p>Stay informed in real time of anomalies, non-compliance, or situations requiring your attention.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-css">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(231, 76, 60, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>for CSS</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  One sentence is all it takes to manage everything
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Global performance all schools this quarter</div>
              <div className="ecopulse-mini-cmd">Compare nutritional compliance by administration</div>
              <div className="ecopulse-mini-cmd">Consolidated annual report for the board</div>
              <div className="ecopulse-mini-cmd">Critical network alerts this week</div>
            </div>
            <button onClick={() => navigate('/en/ecopulse')} className="ecopulse-mini-link">
              Discover all EcoPulse commands <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>

              <div className="footer-column">
                <h4>Platform</h4>
                <a onClick={() => navigate('/en/platform')}>Architecture</a>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>&copy; 2025 EcoLunch. All rights reserved.</span>
              <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontWeight: 600, textDecoration: 'none'}}>ecotechsysteme.ca</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageCSSEN;
